/**
 * -----------------------------------------------------------------------------
 * Project  : ReelConsensus app - Frontend Page: Login
 * File     : Login.js
 * Author   : Zachary Dry
 * Created  : 2025-09-18
 * Updated  : 2025-09-19
 * Version  : 1.0
 * License: Proprietary (Company Confidential)
 * -----------------------------------------------------------------------------
 * Description:
 *   React page component rendering the Login view and coordinating data fetching.
 *   Exports: Login
 *   
 *
 * Dependencies:
 *   - ../context/AuthContext
 *   - @mui/material
 *   - react
 *   - react-router-dom
 *
 * Usage:
 *   Use in Router: <Route path="/login" element={<Login />} />
 * -----------------------------------------------------------------------------
 */
import React, { useEffect, useState } from 'react';
import { TextField, Button, Paper, Typography, Box, Stack } from '@mui/material';
import { buildApiUrl } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';

const Login = () => {
  const { login, user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ username: '', password: '' });
  const [error, setError] = useState('');

  // If token is present in hash, force a user refresh then redirect
  useEffect(() => {
    const hash = window.location.hash || '';
    const hasToken = hash.includes('token=');
    if (hasToken) {
      (async () => {
        try {
          await refreshUser();
        } finally {
          window.location.replace('/');
        }
      })();
      return;
    }
    if (user) navigate('/');
  }, [user, navigate, refreshUser, location]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!window.isSecureContext) {
      setError('Sign-in is only allowed over a secure connection (HTTPS). Please use https:// to protect your password.');
      return;
    }
    try {
      await login({ ...form, username: (form.username || '').toLowerCase().trim() });
      navigate('/');
    } catch (e) {
      const data = e?.response?.data;
      const msg = data?.error || 'Login failed';
      const detail = data?.message;
      setError(detail ? `${msg}: ${detail}` : msg);
    }
  };

  return (
    <Paper sx={{ p: 3, maxWidth: 400 }}>
      <Typography variant="h5" gutterBottom>Login</Typography>
      <Box component="form" onSubmit={onSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField label="Username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} required helperText="We lowercase usernames automatically." />
        <TextField label="Password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
        {error && <Typography color="error">{error}</Typography>}
        <Button type="submit" variant="contained">Login</Button>
      </Box>
      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <Button onClick={() => {
          const cid = process.env.REACT_APP_DISCORD_CLIENT_ID;
          const url = buildApiUrl('/api/auth/discord/login') + (cid ? `?client_id=${encodeURIComponent(cid)}` : '');
          window.location.href = url;
        }} variant="outlined">Login with Discord</Button>
      </Stack>
    </Paper>
  );
};

export default Login;


